import type { GaVueComponent } from "@/common/vueUtils";
import { Filter } from "@/components/sorting-filtering-paging/Filter";
import { GaButton } from "@/components/general/GaButton";
import type { Store } from "@/common/storeUtils";
import { GaChip } from "@/components/general/GaChip";
import { FilterChip } from "@/components/sorting-filtering-paging/FilterChip";
import {
  type ActionableFiltersForm,
  type ExpertVideoFiltersForm,
  type FormFilter,
  type FormSorting,
  Language,
  type MiabFiltersForm,
  type SummaryFiltersForm,
  type SummarySort,
  type OnlineContentFiltersForm,
  OnlineContentFormatType,
} from "@newgenerated/shared/schema";
import type { MultiSelectStateProps } from "@/components/form/GaFormFieldMultiSelect";
import type { DeepReadonly } from "vue";
import { assert, assertIsDefined } from "@utils/assertion";
import { getActiveFilterValues } from "@/components/search/fullSearchStore";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";

export type FormFilterExtended = FormFilter & {
  isCollapsed: boolean;
  multiSelectProps: MultiSelectStateProps<string> | null;
};

export type ContentTypeFilterProps = {
  formFilters: FormFilterExtended[];
  formFilterExtended: boolean;
};

function getMultiSelectProps(formFilter: DeepReadonly<FormFilter>): MultiSelectStateProps<string> | null {
  if (formFilter.type === "MULTISELECT") {
    return {
      searchTerm: "",
      options: [...formFilter.options],
      showSearch: false,
    };
  }
  return null;
}

export const toFormFilterExtended = (formFilters: DeepReadonly<FormFilter[]>): FormFilterExtended[] => {
  return formFilters.map(
    (filter) =>
      ({
        activeValues: [...filter.activeValues],
        identifier: filter.identifier,
        label: filter.label,
        name: filter.name,
        options: [...filter.options],
        priority: filter.priority,
        type: filter.type,
        isCollapsed: true,
        multiSelectProps: getMultiSelectProps(filter),
      }) satisfies FormFilterExtended,
  );
};

function convertToNumbers(input: string[]): number[] {
  return input.map((value) => {
    const number = parseInt(value);
    if (Number.isNaN(number)) {
      throw Error("could not parse number");
    }
    return number;
  });
}

function convertToLanguages(values: string[]): Language[] {
  return values.map((value) => {
    assert(Language.getValues().includes(value as Language));
    return value as Language;
  });
}

function convertOnlineContentFormatType(values: string[]): OnlineContentFormatType[] {
  return values.map((value) => {
    assert(OnlineContentFormatType.getValues().includes(value as OnlineContentFormatType));
    return value as OnlineContentFormatType;
  });
}

export const convertToSummaryFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): SummaryFiltersForm => {
  const audioFilter = getActiveFilterValues(filters, "AUDIO").at(0);
  const miabFilter = getActiveFilterValues(filters, "MIAB").at(0);
  assert(audioFilter === "true" || audioFilter === "false" || audioFilter === undefined);
  assert(miabFilter === "true" || miabFilter === "false" || miabFilter === undefined);
  return {
    qualityFormFilter: [...getActiveFilterValues(filters, "QUALITY")],
    ratingFormFilter: convertToNumbers([...getActiveFilterValues(filters, "RATING")]),
    sourceFormFilter: [...getActiveFilterValues(filters, "SOURCE")],
    languageFormFilter: [...getActiveFilterValues(filters, "LANGUAGE")].map((language) => Language.getValues().find((l) => l === language) ?? "en"),
    audioFormFilter: audioFilter === "true",
    publicationDateFormFilter: convertToNumbers([...getActiveFilterValues(filters, "PUBLICATION_DATE")]),
    miabFormFilter: miabFilter === "true",
  };
};

export const convertToActionableFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): ActionableFiltersForm => {
  return { languageFormFilter: convertToLanguages([...getActiveFilterValues(filters, "LANGUAGE")]) };
};

export const convertToMiabFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): MiabFiltersForm => {
  return { languageFormFilter: convertToLanguages([...getActiveFilterValues(filters, "LANGUAGE")]) };
};

export const convertToExpertVideoFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): ExpertVideoFiltersForm => {
  return { languageFormFilter: convertToLanguages([...getActiveFilterValues(filters, "LANGUAGE")]) };
};

export const convertToOnlineContentFiltersForm = (filters: DeepReadonly<FormFilterExtended[]>): OnlineContentFiltersForm => {
  const expertPickFilter = getActiveFilterValues(filters, "EXPERT_PICK").at(0);
  assert(expertPickFilter === "true" || expertPickFilter === "false" || expertPickFilter === undefined);
  return {
    expertPickFormFilter: expertPickFilter === undefined ? null : expertPickFilter === "true",
    formatTypeFilter: convertOnlineContentFormatType([...getActiveFilterValues(filters, "VERIFAI_FORMAT_TYPE")]),
  };
};

function ContentFiltering(props: { filterStore: Store<ContentTypeFilterProps>; applyFilters: () => void; ctx: { t: TranslationFn } }): GaVueComponent {
  const formFilterStore = props.filterStore.sub("formFilters");
  return (
    <div class={["collapse mb-2", props.filterStore.sub("formFilterExtended").get() ? "show" : ""]}>
      <div>
        <div class="filtering-dropdown">
          <div class="filtering-dropdown__left-col">
            {formFilterStore
              .get()
              .filter((filter) => filter.priority === "PRIMARY")
              .map((filter) => formFilterStore.sub(formFilterStore.get().findIndex((f) => f.identifier === filter.identifier)))
              .map((filter) => (
                <Filter filter={filter} />
              ))}
          </div>
          <div class="filtering-dropdown__right-col">
            {formFilterStore
              .get()
              .filter((filter) => filter.priority === "SECONDARY")
              .map((filter) => formFilterStore.sub(formFilterStore.get().findIndex((f) => f.identifier === filter.identifier)))
              .map((filter) => (
                <Filter filter={filter} />
              ))}
          </div>
          <div>
            <GaButton
              onClick={() => {
                props.applyFilters();
                props.filterStore.sub("formFilterExtended").set(false);
              }}>
              {props.ctx.t("general:button.apply")}
            </GaButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export type FormSortingExtended = {
  formSorting: FormSorting;
  collapsed: boolean;
};

export const convertToSummarySort = (sorting: DeepReadonly<FormSortingExtended>): SummarySort => {
  switch (sorting.formSorting.activeValue) {
    case "ACTIVATED_AT":
    case "onlineDate":
      return "ACTIVATED_AT";
    case "POPULARITY":
    case "bestselling":
      return "POPULARITY";
    case "TITLE":
    case "title":
      return "TITLE";
    case "PUBLICATION_DATE":
    case "publishDate":
      return "PUBLICATION_DATE";
    case "RATING":
    case "rating":
      return "RATING";
    case "RELEVANCE":
    case "relevance":
      return "RELEVANCE";
    default:
      return "RELEVANCE";
  }
};

function ContentSorting(props: { sortingStore: Store<FormSortingExtended>; applySorting: () => void; ctx: { currentLanguage: Language } }): GaVueComponent {
  const sortingText = props.ctx.currentLanguage === "ar" ? "فرز" : "Sorting";

  return (
    <div class={["collapse mb-2", !props.sortingStore.sub("collapsed").get() ? "show" : ""]}>
      <div class="sorting-dropdown__wrapper">
        <div class="sorting-dropdown">
          <h5 class="sorting-dropdown__header" id="sorting-${uid}">
            {sortingText}
          </h5>
          <div class="sorting-dropdown__body">
            <ul class="list-unstyled sorting-dropdown__chip-list" aria-labelledby="sorting">
              {props.sortingStore
                .sub("formSorting")
                .get()
                .options.map((option) => {
                  const active = option.value === props.sortingStore.sub("formSorting").get().activeValue;
                  return (
                    <li>
                      <GaChip
                        active={active}
                        type="checkbox"
                        changeHandler={() => {
                          props.sortingStore.sub("formSorting").sub("activeValue").set(option.value);
                          props.applySorting();
                          props.sortingStore.sub("collapsed").set(true);
                        }}>
                        {option.label}
                      </GaChip>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function SortingChip(props: { sortingStore: Store<FormSortingExtended> }): GaVueComponent {
  const activeSorting = props.sortingStore
    .sub("formSorting")
    .get()
    .options.find((option) => option.value === props.sortingStore.sub("formSorting").get().activeValue);
  assertIsDefined(activeSorting);

  return (
    <GaChip icon="ico-sort-down" type={"checkbox"} changeHandler={() => props.sortingStore.sub("collapsed").set(!props.sortingStore.sub("collapsed").get())}>
      {activeSorting.label}
    </GaChip>
  );
}

export function ContentFilteringSorting(props: { filterStore?: Store<ContentTypeFilterProps>; sortingStore?: Store<FormSortingExtended>; apply: () => void; ctx: { currentLanguage: Language; t: TranslationFn } }): GaVueComponent {
  const filterStore = props.filterStore;
  return (
    <>
      <div class="d-flex flex-wrap gap-2 mb-3">
        {props.sortingStore === undefined ? null : <SortingChip sortingStore={props.sortingStore} />}
        {filterStore === undefined ? null : (
          <>
            <GaChip icon="ico-equalizer" type={"checkbox"} changeHandler={() => filterStore.sub("formFilterExtended").set(!filterStore.sub("formFilterExtended").get())}>
              {props.ctx.t("summary:filtersTitle")}
            </GaChip>
            {filterStore
              .sub("formFilters")
              .get()
              .filter((filter) => filter.activeValues.length > 0)
              .map((filter) =>
                filterStore.sub("formFilters").sub(
                  filterStore
                    .sub("formFilters")
                    .get()
                    .findIndex((f) => f.identifier === filter.identifier),
                ),
              )
              .map((filter) => (
                <FilterChip filter={filter} applyFilters={props.apply} />
              ))}
          </>
        )}
      </div>
      {props.sortingStore === undefined ? null : <ContentSorting sortingStore={props.sortingStore} applySorting={props.apply} ctx={props.ctx} />}
      {props.filterStore === undefined ? null : <ContentFiltering filterStore={props.filterStore} applyFilters={props.apply} ctx={props.ctx} />}
    </>
  );
}
